import React, { useEffect, useState } from 'react';
import {
  Card, Form, Input, InputNumber, Switch, Button, Space,
  Table, Tag, Modal, App, Tabs, Alert, Select, Divider,
  Tooltip, Badge, Descriptions
} from 'antd';
import {
  ApiOutlined, PlusOutlined, EditOutlined, DeleteOutlined,
  SaveOutlined, KeyOutlined, LockOutlined, CheckCircleOutlined,
  CloseCircleOutlined, InfoCircleOutlined, ThunderboltOutlined,
  EyeOutlined, CopyOutlined, ReloadOutlined, RocketOutlined,
  VideoCameraOutlined, PictureOutlined, FileTextOutlined
} from '@ant-design/icons';
import { apiClient } from '@/services/api';

const { Option } = Select;
const { TextArea } = Input;

interface VideoApiProvider {
  provider: string;
  name: string;
  enabled: boolean;
  priority: number;
  features: {
    textToVideo: boolean;
    imageToVideo: boolean;
    videoToVideo: boolean;
    audioGeneration: boolean;
    maxDuration: number;
    supportedAspectRatios: string[];
  };
  models: string[];
  creditCosts?: {
    textToVideo: number;
    imageToVideo: number;
    videoToVideo?: number;
  };
}

interface VideoApiConfig {
  provider: string;
  enabled: boolean;
  priority: number;
  models?: string[];
  creditCosts?: {
    textToVideo: number;
    imageToVideo: number;
    videoToVideo?: number;
  };
  config: {
    enabled: boolean;
    apiKey: string;
    host: string;
    model?: string;
    [key: string]: any;
  };
}

const APIConfigManager: React.FC = () => {
  const { message: antdMessage, modal } = App.useApp();
  const [loading, setLoading] = useState(false);

  // 视频 API 提供商状态
  const [videoProviders, setVideoProviders] = useState<VideoApiProvider[]>([]);
  const [videoConfigs, setVideoConfigs] = useState<VideoApiConfig[]>([]);
  const [testingProvider, setTestingProvider] = useState<string>('');
  const [editProviderModal, setEditProviderModal] = useState(false);
  const [currentProvider, setCurrentProvider] = useState<VideoApiConfig | null>(null);
  const [currentApiType, setCurrentApiType] = useState<'video' | 'image' | 'text'>('video');

  // 图片 API 提供商状态
  const [imageProviders, setImageProviders] = useState<any[]>([]);
  const [imageConfigs, setImageConfigs] = useState<any[]>([]);

  // 文字 API 提供商状态
  const [textProviders, setTextProviders] = useState<any[]>([]);
  const [textConfigs, setTextConfigs] = useState<any[]>([]);

  const [providerForm] = Form.useForm();

  // 加载视频 API 提供商
  const loadVideoProviders = async () => {
    try {
      // 🔥 修复：使用公开API而不是管理员API
      const [providersRes, configRes] = await Promise.all([
        apiClient.get('/video/models'),
        apiClient.get('/admin/video-api/config')
      ]);

      // 🔥 修复：处理公开API的响应格式
      const publicData = providersRes?.success ? providersRes.data : { models: [] };
      const allModels = publicData.models || [];
      const configs = configRes?.success ? (configRes.data.providers || []) : [];

      console.log('🔍 [前端] 所有模型:', allModels.map(m => ({ provider: m.provider, name: m.name })));
      console.log('🔍 [前端] 配置的服务商:', configs.map(c => ({ provider: c.provider, models: c.models })));

      // 只显示实际配置的服务商，不显示硬编码的所有服务商
      const processedProviders = [];
      
      for (const config of configs) {
        // 从公开API的模型中查找对应的提供商信息
        const providerModels = allModels.filter((m: any) => m.provider === config.provider);
        const providerMeta = {
          provider: config.provider,
          name: config.provider,
          features: {
            textToVideo: true,
            imageToVideo: true,
            videoToVideo: false,
            audioGeneration: false,
            maxDuration: 10,
            supportedAspectRatios: ['16:9', '9:16', '1:1']
          },
          models: providerModels.map((m: any) => m.name)
        };
        
        // 确定最终使用的模型 - 优先显示配置的模型
        let finalModels = [];
        if (config.models && config.models.length > 0) {
          // 如果有配置的模型，使用配置的模型
          finalModels = config.models;
        } else if (config.config?.model && config.config.model.trim()) {
          // 如果没有配置的模型列表，但有默认模型，使用默认模型
          finalModels = [config.config.model.trim()];
        } else if (providerMeta?.models && providerMeta.models.length > 0) {
          // 最后才使用支持的所有模型
          finalModels = providerMeta.models;
        }
        
        console.log(`🔍 [前端] 处理已配置服务商 ${config.provider}:`, {
          hasConfig: true,
          configModels: config.models,
          providerMetaModels: providerMeta?.models,
          finalModels
        });
        
        processedProviders.push({
          ...(providerMeta || { 
            provider: config.provider, 
            name: config.provider,
            features: {},
            models: []
          }),
          enabled: config.enabled || false,
          priority: config.priority || 99,
          models: finalModels,
          creditCosts: config.creditCosts || {
            textToVideo: 20,
            imageToVideo: 25
          },
          config: config.config || {
            enabled: false,
            apiKey: '',
            host: '',
            model: ''
          }
      });
      }
      
      const configuredProviders = processedProviders;

      setVideoProviders(configuredProviders);
      setVideoConfigs(configs);
    } catch (error: any) {
      console.error('加载视频API配置失败:', error);
      setVideoProviders([]);
      setVideoConfigs([]);
    }
  };

  // 加载图片 API 提供商
  const loadImageProviders = async () => {
    try {
      // 🔥 修复：使用公开API而不是管理员API
      const [providersRes, configRes] = await Promise.all([
        apiClient.get('/image/models'),
        apiClient.get('/admin/image-api/config')
      ]);

      // 🔥 修复：处理公开API的响应格式
      const publicData = providersRes?.success ? providersRes.data : { providers: [], models: [] };
      const allProviders = publicData.providers || [];
      const allModels = publicData.models || [];
      const configs = configRes?.success ? (configRes.data.providers || []) : [];

      // 只显示config中存在的提供商（已配置的）
      const processedProviders = [];
      
      for (const config of configs) {
        // 从公开API的模型中查找对应的提供商信息
        const providerModels = allModels.filter((m: any) => m.provider === config.provider);
        const providerMeta = allProviders.find((p: any) => p.provider === config.provider) || {
          provider: config.provider,
          name: config.provider,
          features: {},
          models: providerModels.map((m: any) => m.name)
        };
        
        // 确定最终使用的模型 - 优先显示配置的模型
        let finalModels = [];
        if (config.models && config.models.length > 0) {
          // 如果有配置的模型，使用配置的模型
          finalModels = config.models;
        } else if (config.config?.model && config.config.model.trim()) {
          // 如果没有配置的模型列表，但有默认模型，使用默认模型
          finalModels = [config.config.model.trim()];
        } else if (providerMeta?.models && providerMeta.models.length > 0) {
          // 最后才使用支持的所有模型
          finalModels = providerMeta.models;
        }
        
        console.log(`🔍 [前端] 处理已配置图片服务商 ${config.provider}:`, {
          hasConfig: true,
          configModels: config.models,
          providerMetaModels: providerMeta?.models,
          finalModels
        });
        
        processedProviders.push({
          ...(providerMeta || { 
            provider: config.provider, 
            name: config.provider,
            features: {},
            models: []
          }),
          enabled: config.enabled || false,
          priority: config.priority || 99,
          models: finalModels,
          creditCosts: config.creditCosts || {
            textToImage: 10
          },
          config: config.config || {
            enabled: false,
            apiKey: '',
            host: '',
            model: ''
          }
      });
      }

      setImageProviders(processedProviders);
      setImageConfigs(configs);
    } catch (error: any) {
      console.error('加载图片API配置失败:', error);
      setImageProviders([]);
      setImageConfigs([]);
    }
  };

  // 加载文字 API 提供商
  const loadTextProviders = async () => {
    try {
      const [providersRes, configRes] = await Promise.all([
        apiClient.get('/admin/text-api/providers'),
        apiClient.get('/admin/text-api/config')
      ]);

      console.log('📥 [前端] 加载文字API提供商响应:', providersRes);
      console.log('📥 [前端] 加载文字API配置响应:', configRes);

      // providers是所有可用的提供商类型（系统内置）
      const allProviders = providersRes?.success ? (providersRes.data.providers || []) : [];
      // configs是用户实际配置的提供商
      const configs = configRes?.success ? (configRes.data.providers || []) : [];

      console.log('📊 [前端] 所有可用文字提供商:', allProviders);
      console.log('📊 [前端] 用户配置的提供商:', configs);

      // 仅展示用户已配置的提供商，优先合并内置元数据
      const finalProviders = configs.map((config: any) => {
        const providerMeta = allProviders.find((p: any) => p.provider === config.provider);
        
        const mergedConfig = {
          ...(providerMeta?.config || {}),
          ...(config.config || {})
        };

        if (!mergedConfig.host && mergedConfig.baseUrl) {
          mergedConfig.host = mergedConfig.baseUrl;
        }
        if (!mergedConfig.baseUrl && mergedConfig.host) {
          mergedConfig.baseUrl = mergedConfig.host;
        }

        let finalModels: string[] = [];
        if (config.models?.length) {
          finalModels = config.models;
        } else if (mergedConfig?.model && String(mergedConfig.model).trim()) {
          finalModels = [String(mergedConfig.model).trim()];
        } else if (providerMeta?.models?.length) {
          finalModels = providerMeta.models;
        }
        
        const mergedProvider = {
          ...(providerMeta || {}),
          ...config,
          name: config.name || providerMeta?.name || config.provider,
          models: finalModels,
          creditCosts: config.creditCosts || providerMeta?.creditCosts || { textGeneration: 5 },
          config: mergedConfig
        };

        console.log(`🔍 [前端] 处理文字服务商 ${config.provider}:`, {
          hasProviderMeta: Boolean(providerMeta),
          finalModels
        });
        
        return mergedProvider;
      });

      console.log('📊 [前端] 最终显示的文字提供商:', finalProviders);

      setTextProviders(finalProviders);
      setTextConfigs(configs);
    } catch (error: any) {
      console.error('❌ 加载文字API配置失败:', error);
      setTextProviders([]);
      setTextConfigs([]);
    }
  };

  useEffect(() => {
    loadVideoProviders();
    loadImageProviders();
    loadTextProviders();
  }, []);

  // 测试视频 API 提供商
  const handleTestVideoProvider = async (provider: string) => {
    setTestingProvider(provider);
    const hideLoading = antdMessage.loading(`正在测试 ${provider} 配置...`, 0);

    try {
      const encodedProvider = encodeURIComponent(provider);
      const res = await apiClient.post(`/admin/video-api/test/${encodedProvider}`);

      hideLoading();

      if (res?.success) {
        modal.success({
          title: '✅ 测试成功',
          content: `${provider} 配置正确，API 连接正常！`
        });
      } else {
        throw new Error(res?.message || '测试失败');
      }
    } catch (error: any) {
      hideLoading();
      modal.error({
        title: '❌ 测试失败',
        content: (
          <div>
            <p>{error?.message || '配置测试失败'}</p>
            <p className="text-xs text-gray-500 mt-2">
              请检查：
              <br />• API Key 是否正确
              <br />• 网络连接是否正常
              <br />• API 服务是否可用
            </p>
          </div>
        )
      });
      console.error(`测试提供商失败:`, error);
    } finally {
      setTestingProvider('');
    }
  };

  // 切换视频提供商启用状态
  const handleToggleVideoProvider = async (provider: string, enabled: boolean) => {
    try {
      const encodedProvider = encodeURIComponent(provider);
      const response = await apiClient.patch(`/admin/video-api/provider/${encodedProvider}`, { enabled });
      
      if (response?.success !== false) {
        antdMessage.success(`${provider} 已${enabled ? '启用' : '禁用'}`);
        await loadVideoProviders();
      } else {
        throw new Error(response?.message || '操作失败');
      }
    } catch (error: any) {
      antdMessage.error('操作失败: ' + (error?.message || '未知错误'));
      console.error(`切换提供商状态失败:`, error);
      // 重新加载以恢复正确状态
      await loadVideoProviders();
    }
  };

  // 更新视频提供商优先级
  const handleVideoProviderPriorityChange = async (provider: string, priority: number) => {
    try {
      const encodedProvider = encodeURIComponent(provider);
      const response = await apiClient.patch(`/admin/video-api/provider/${encodedProvider}`, { priority });
      
      if (response?.success !== false) {
        antdMessage.success('优先级已更新');
        await loadVideoProviders();
      } else {
        throw new Error(response?.message || '更新失败');
      }
    } catch (error: any) {
      antdMessage.error('更新失败: ' + (error?.message || '未知错误'));
      console.error(`更新优先级失败:`, error);
      // 重新加载以恢复正确状态
      await loadVideoProviders();
    }
  };

  // 更新视频提供商模型配置
  const handleVideoProviderModelsChange = async (provider: string, models: string[]) => {
    try {
      console.log(`🔧 [前端] 更新模型配置: ${provider} - ${models.join(', ')}`);
      
      const encodedProvider = encodeURIComponent(provider);
      const requestData = { models };
      
      console.log(`📤 [前端] 发送请求:`, requestData);
      const response = await apiClient.patch(`/admin/video-api/provider/${encodedProvider}`, requestData);
      
      console.log(`📥 [前端] 更新模型响应:`, response);
      
      if (response?.success !== false) {
        antdMessage.success('模型配置已更新');
        await loadVideoProviders();
      } else {
        throw new Error(response?.message || '更新失败');
      }
    } catch (error: any) {
      antdMessage.error('更新失败: ' + (error?.message || '未知错误'));
      console.error(`更新模型配置失败:`, error);
      // 重新加载以恢复正确状态
      await loadVideoProviders();
    }
  };

  // 更新视频提供商积分消耗
  const handleCreditCostChange = async (provider: string, costType: string, value: number) => {
    try {
      console.log(`🔧 [前端] 更新积分消耗: ${provider} - ${costType} = ${value}`);
      
      const encodedProvider = encodeURIComponent(provider);
      const requestData = { 
        creditCosts: { [costType]: value }
      };
      
      console.log(`📤 [前端] 发送请求:`, requestData);
      
      const response = await apiClient.patch(`/admin/video-api/provider/${encodedProvider}`, requestData);
      
      console.log(`📥 [前端] 响应:`, response);
      
      if (response?.success !== false) {
        antdMessage.success('积分消耗已更新');
        await loadVideoProviders();
      } else {
        throw new Error(response?.message || '更新失败');
      }
    } catch (error: any) {
      antdMessage.error('更新失败: ' + (error?.message || '未知错误'));
      console.error(`更新积分消耗失败:`, error);
      // 重新加载以恢复正确状态
      await loadVideoProviders();
    }
  };

  // 编辑视频提供商
  const handleEditVideoProvider = (config: VideoApiConfig) => {
    setCurrentProvider(config);
    setCurrentApiType('video');
    
    // 设置表单值，如果模型列表存在且只有一个模型，则作为默认模型显示
    const formValues = {
      ...config.config,
      model: config.models && config.models.length === 1 ? config.models[0] : config.config.model
    };
    
    providerForm.setFieldsValue(formValues);
    setEditProviderModal(true);
  };

  // 保存视频提供商编辑
  const handleSaveVideoProvider = async () => {
    try {
      setLoading(true);
      const values = await providerForm.validateFields();

      let apiEndpoint = '/admin/video-api';
      if (currentApiType === 'image') {
        apiEndpoint = '/admin/image-api';
      } else if (currentApiType === 'text') {
        apiEndpoint = '/admin/text-api';
      }

      let response;
      if (currentProvider) {
        // 编辑现有提供商
        const encodedProvider = encodeURIComponent(currentProvider.provider);
        const updateData: any = {
          config: values
        };
        
        // 如果配置了默认模型，将其作为模型列表保存
        if (values.model && values.model.trim()) {
          updateData.models = [values.model.trim()];
        }
        
        response = await apiClient.patch(`${apiEndpoint}/provider/${encodedProvider}`, updateData);
      } else {
        // 添加新提供商
        const newProvider: any = {
          provider: values.provider,
          enabled: false,
          priority: values.priority || 99,
          config: {
            enabled: false,
            apiKey: values.apiKey,
            host: values.host,
            model: values.model
          }
        };
        
        // 如果配置了默认模型，将其作为模型列表保存
        if (values.model && values.model.trim()) {
          newProvider.models = [values.model.trim()];
        }
        
        response = await apiClient.post(`${apiEndpoint}/provider`, newProvider);
      }

      // 检查响应是否成功
      if (response?.success !== false) {
        antdMessage.success(currentProvider ? '配置已保存' : '提供商添加成功');
        
        // 关闭Modal并重置表单
      setEditProviderModal(false);
        providerForm.resetFields();
        setCurrentProvider(null);
      
      // 根据类型刷新对应的提供商列表
      if (currentApiType === 'image') {
          await loadImageProviders();
        } else if (currentApiType === 'text') {
          await loadTextProviders();
      } else {
          await loadVideoProviders();
        }
      } else {
        throw new Error(response?.message || (currentProvider ? '保存失败' : '添加失败'));
      }
    } catch (error: any) {
      antdMessage.error((currentProvider ? '保存' : '添加') + '失败: ' + (error?.message || '未知错误'));
      console.error('保存提供商配置失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 删除提供商（通用）
  const handleDeleteProvider = async (provider: string, apiType: 'video' | 'image' | 'text') => {
    const apiTypeMap = {
      video: '视频',
      image: '图片',
      text: '文字'
    };

    modal.confirm({
      title: '确认删除',
      content: `确定要删除${apiTypeMap[apiType]} API 提供商 "${provider}" 吗？删除后将无法恢复。`,
      okText: '确定删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        const hideLoading = antdMessage.loading('正在删除...', 0);
        try {
          console.log(`🗑️ [前端] 准备删除${apiTypeMap[apiType]}提供商: ${provider}`);
          
          const encodedProvider = encodeURIComponent(provider);
          console.log(`🔗 [前端] 编码后的provider: ${encodedProvider}`);
          console.log(`🔗 [前端] 删除URL: /admin/${apiType}-api/provider/${encodedProvider}`);
          
          const response = await apiClient.delete(`/admin/${apiType}-api/provider/${encodedProvider}`);
          
          console.log(`📥 [前端] 删除响应:`, response);
          hideLoading();
          
          // 检查响应是否成功
          if (response?.success !== false) {
            console.log(`✅ [前端] 后端删除成功，开始更新前端state`);
            
            // 立即从前端state中移除，提供即时反馈
            if (apiType === 'video') {
              setVideoProviders(prev => {
                const filtered = prev.filter(p => p.provider !== provider);
                console.log(`📊 [前端] 过滤前:`, prev.length, `个提供商`);
                console.log(`📊 [前端] 过滤后:`, filtered.length, `个提供商`);
                return filtered;
              });
              setVideoConfigs(prev => prev.filter(c => c.provider !== provider));
            } else if (apiType === 'image') {
              setImageProviders(prev => {
                const filtered = prev.filter(p => p.provider !== provider);
                console.log(`📊 [前端] 过滤前:`, prev.length, `个提供商`);
                console.log(`📊 [前端] 过滤后:`, filtered.length, `个提供商`);
                return filtered;
              });
              setImageConfigs(prev => prev.filter(c => c.provider !== provider));
            } else if (apiType === 'text') {
              setTextProviders(prev => {
                const filtered = prev.filter(p => p.provider !== provider);
                console.log(`📊 [前端] 过滤前:`, prev.length, `个提供商`);
                console.log(`📊 [前端] 过滤后:`, filtered.length, `个提供商`);
                return filtered;
              });
              setTextConfigs(prev => prev.filter(c => c.provider !== provider));
            }
            
            antdMessage.success('提供商已删除');
            
            console.log(`🔄 [前端] 开始从后端重新加载数据...`);
            // 再次从后端刷新确保数据一致
            if (apiType === 'video') {
              await loadVideoProviders();
            } else if (apiType === 'image') {
              await loadImageProviders();
            } else if (apiType === 'text') {
              await loadTextProviders();
            }
            console.log(`✅ [前端] 数据刷新完成`);
          } else {
            throw new Error(response?.message || '删除失败');
          }
        } catch (error: any) {
          hideLoading();
          antdMessage.error('删除失败: ' + (error?.message || '未知错误'));
          console.error('❌ [前端] 删除提供商失败:', error);
        }
      }
    });
  };

  const handleDeleteVideoProvider = (provider: string) => handleDeleteProvider(provider, 'video');
  const handleDeleteImageProvider = (provider: string) => handleDeleteProvider(provider, 'image');
  const handleDeleteTextProvider = (provider: string) => handleDeleteProvider(provider, 'text');

  // 图片提供商处理函数
  const handleToggleImageProvider = async (provider: string, enabled: boolean) => {
    try {
      const encodedProvider = encodeURIComponent(provider);
      const response = await apiClient.patch(`/admin/image-api/provider/${encodedProvider}`, { enabled });
      
      if (response?.success !== false) {
        antdMessage.success(`${provider} 已${enabled ? '启用' : '禁用'}`);
        await loadImageProviders();
      } else {
        throw new Error(response?.message || '操作失败');
      }
    } catch (error: any) {
      antdMessage.error('操作失败: ' + (error?.message || '未知错误'));
      console.error(`切换图片提供商状态失败:`, error);
      // 重新加载以恢复正确状态
      await loadImageProviders();
    }
  };

  const handleImageProviderPriorityChange = async (provider: string, priority: number) => {
    try {
      const encodedProvider = encodeURIComponent(provider);
      const response = await apiClient.patch(`/admin/image-api/provider/${encodedProvider}`, { priority });
      
      if (response?.success !== false) {
        antdMessage.success('优先级已更新');
        await loadImageProviders();
      } else {
        throw new Error(response?.message || '更新失败');
      }
    } catch (error: any) {
      antdMessage.error('更新失败: ' + (error?.message || '未知错误'));
      console.error(`更新图片提供商优先级失败:`, error);
      // 重新加载以恢复正确状态
      await loadImageProviders();
    }
  };

  // 更新图片提供商积分消耗
  const handleImageCreditCostChange = async (provider: string, costType: string, value: number) => {
    try {
      const encodedProvider = encodeURIComponent(provider);
      const response = await apiClient.patch(`/admin/image-api/provider/${encodedProvider}`, { 
        creditCosts: { [costType]: value }
      });
      
      if (response?.success !== false) {
        antdMessage.success('积分消耗已更新');
        await loadImageProviders();
      } else {
        throw new Error(response?.message || '更新失败');
      }
    } catch (error: any) {
      antdMessage.error('更新失败: ' + (error?.message || '未知错误'));
      console.error(`更新图片积分消耗失败:`, error);
      // 重新加载以恢复正确状态
      await loadImageProviders();
    }
  };

  const handleEditImageProvider = (record: any) => {
    setCurrentProvider(record);
    setCurrentApiType('image');
    providerForm.setFieldsValue(record.config || {
      apiKey: '',
      host: 'https://grsai.dakka.com.cn',
      model: 'nano-banana-fast'
    });
    setEditProviderModal(true);
  };

  const handleTestImageProvider = async (provider: string) => {
    try {
      antdMessage.loading('正在测试连接...', 0);
      const encodedProvider = encodeURIComponent(provider);
      const response = await apiClient.post(`/admin/image-api/test/${encodedProvider}`);
      antdMessage.destroy();
      if (response.success) {
        antdMessage.success('测试成功！配置正常');
      } else {
        antdMessage.error('测试失败: ' + (response.message || '未知错误'));
      }
    } catch (error: any) {
      antdMessage.destroy();
      antdMessage.error('测试失败: ' + (error?.message || '未知错误'));
      console.error(`测试图片提供商失败:`, error);
    }
  };

  // 文字提供商处理函数
  const handleToggleTextProvider = async (provider: string, enabled: boolean) => {
    try {
      const encodedProvider = encodeURIComponent(provider);
      const response = await apiClient.patch(`/admin/text-api/provider/${encodedProvider}`, { enabled });
      
      if (response?.success !== false) {
        antdMessage.success(`${provider} 已${enabled ? '启用' : '禁用'}`);
        await loadTextProviders();
      } else {
        throw new Error(response?.message || '操作失败');
      }
    } catch (error: any) {
      antdMessage.error('操作失败: ' + (error?.message || '未知错误'));
      console.error(`切换文字提供商状态失败:`, error);
      // 重新加载以恢复正确状态
      await loadTextProviders();
    }
  };

  const handleTextProviderPriorityChange = async (provider: string, priority: number) => {
    try {
      const encodedProvider = encodeURIComponent(provider);
      const response = await apiClient.patch(`/admin/text-api/provider/${encodedProvider}`, { priority });
      
      if (response?.success !== false) {
        antdMessage.success('优先级已更新');
        await loadTextProviders();
      } else {
        throw new Error(response?.message || '更新失败');
      }
    } catch (error: any) {
      antdMessage.error('更新失败: ' + (error?.message || '未知错误'));
      console.error(`更新文字提供商优先级失败:`, error);
      // 重新加载以恢复正确状态
      await loadTextProviders();
    }
  };

  // 更新文字提供商积分消耗
  const handleTextCreditCostChange = async (provider: string, costType: string, value: number) => {
    try {
      const encodedProvider = encodeURIComponent(provider);
      const response = await apiClient.patch(`/admin/text-api/provider/${encodedProvider}`, { 
        creditCosts: { [costType]: value }
      });
      
      if (response?.success !== false) {
        antdMessage.success('积分消耗已更新');
        await loadTextProviders();
      } else {
        throw new Error(response?.message || '更新失败');
      }
    } catch (error: any) {
      antdMessage.error('更新失败: ' + (error?.message || '未知错误'));
      console.error(`更新文字积分消耗失败:`, error);
      // 重新加载以恢复正确状态
      await loadTextProviders();
    }
  };

  const handleEditTextProvider = (record: any) => {
    setCurrentProvider(record);
    setCurrentApiType('text');
    providerForm.setFieldsValue(record.config || {
      apiKey: '',
      host: '',
      model: ''
    });
    setEditProviderModal(true);
  };

  const handleTestTextProvider = async (provider: string) => {
    try {
      antdMessage.loading('正在测试连接...', 0);
      const encodedProvider = encodeURIComponent(provider);
      const response = await apiClient.post(`/admin/text-api/test/${encodedProvider}`);
      antdMessage.destroy();
      if (response.success) {
        antdMessage.success('测试成功！配置正常');
      } else {
        antdMessage.error('测试失败: ' + (response.message || '未知错误'));
      }
    } catch (error: any) {
      antdMessage.destroy();
      antdMessage.error('测试失败: ' + (error?.message || '未知错误'));
      console.error(`测试文字提供商失败:`, error);
    }
  };

  // 视频提供商表格列
  const videoProviderColumns = [
    {
      title: '提供商',
      dataIndex: 'name',
      key: 'name',
      width: 250,
      render: (name: string, record: VideoApiProvider) => (
        <Space>
          <VideoCameraOutlined className="text-blue-500" />
          <span className="font-semibold">{name}</span>
          <Tag color={record.enabled ? 'green' : 'default'}>
            {record.enabled ? '已启用' : '未启用'}
          </Tag>
        </Space>
      )
    },
    {
      title: '优先级',
      dataIndex: 'priority',
      key: 'priority',
      width: 120,
      render: (priority: number, record: VideoApiProvider) => (
        <InputNumber
          min={1}
          max={99}
          value={priority}
          onChange={(value) => handleVideoProviderPriorityChange(record.provider, value || 1)}
          disabled={!record.enabled}
          size="small"
          style={{ width: '80px' }}
        />
      )
    },
    {
      title: '配置模型',
      dataIndex: 'models',
      key: 'models',
      width: 250,
      render: (models: string[], record: VideoApiProvider) => (
        <div className="flex flex-wrap gap-1">
          {models && models.length > 0 ? (
            models.map((model, index) => (
              <Tag 
                key={index} 
                color="blue" 
                className="text-xs"
              >
                {model}
              </Tag>
            ))
          ) : (
            <span className="text-gray-400 text-xs">暂无配置模型</span>
          )}
        </div>
      )
    },
    {
      title: '积分消耗',
      key: 'creditCosts',
      width: 180,
      render: (_: any, record: VideoApiProvider) => (
        <Space.Compact style={{ width: '150px' }}>
          <InputNumber
            min={1}
            max={1000}
            value={record.creditCosts?.textToVideo || 20}
            onChange={(value) => handleCreditCostChange(record.provider, 'textToVideo', value || 20)}
            size="small"
            style={{ width: '100%' }}
          />
          <Button disabled size="small" style={{ width: '50px' }}>积分</Button>
        </Space.Compact>
      )
    },
    {
      title: '状态',
      key: 'status',
      width: 120,
      render: (_: any, record: VideoApiProvider) => (
        <Switch
          checked={record.enabled}
          onChange={(checked) => handleToggleVideoProvider(record.provider, checked)}
          checkedChildren="启用"
          unCheckedChildren="禁用"
          size="small"
        />
      )
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_: any, record: VideoApiProvider) => {
        // 查找或创建配置
        let config = videoConfigs.find(c => c.provider === record.provider);
        
        // 如果没有找到配置，创建一个基本配置
        if (!config) {
          config = {
            provider: record.provider,
            enabled: record.enabled,
            priority: record.priority,
            config: {
              enabled: record.enabled,
              apiKey: '',
              host: '',
              model: record.models[0] || ''
            }
          };
        }
        
        return (
          <Space size="small">
            <Button
              type="primary"
              size="small"
              icon={<RocketOutlined />}
              onClick={() => handleTestVideoProvider(record.provider)}
              loading={testingProvider === record.provider}
              disabled={!record.enabled || !config?.config?.apiKey}
              title={!record.enabled ? '请先启用提供商' : !config?.config?.apiKey ? '请先配置 API Key' : '测试连接'}
            >
              测试
            </Button>
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditVideoProvider(config)}
            >
              配置
            </Button>
            <Button
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteVideoProvider(record.provider)}
              disabled={record.enabled}
              title={record.enabled ? '请先禁用提供商' : '删除提供商'}
            >
              删除
            </Button>
          </Space>
        );
      }
    }
  ];

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex items-center space-x-3">
        <ApiOutlined className="text-2xl text-purple-600" />
        <h2 className="text-2xl font-bold text-gray-800">API 接口配置</h2>
      </div>

      <Alert
        message="API配置说明"
        description="管理第三方API密钥、端点配置和访问控制。请妥善保管API密钥，避免泄露。"
        type="warning"
        showIcon
        closable
      />

      <Tabs 
        defaultActiveKey="video-api" 
        size="large"
        items={[
          {
            key: 'video-api',
            label: (
              <span>
                <VideoCameraOutlined />
                视频 API
                <Badge count={videoProviders.filter(p => p.enabled).length} showZero style={{ marginLeft: 8 }} />
              </span>
            ),
            children: (
              <>
          {/* 系统说明 */}
          <Alert
            message="🎬 视频 API 提供商管理"
            description={
              <div>
                <p>• <strong>自动故障转移</strong>：当高优先级 API 失败时，自动切换到备用 API</p>
                <p>• <strong>优先级控制</strong>：数字越小优先级越高（1 最高），系统优先使用高优先级 API</p>
                <p>• <strong>配置测试</strong>：在启用前点击"测试"按钮验证 API 配置是否正确</p>
                <p>• <strong>灵活扩展</strong>：支持添加多个视频 API 提供商，如 Grsai、Minimax、豆包等</p>
              </div>
            }
            type="info"
            showIcon
            className="mb-4"
          />

          {/* 提供商列表 */}
          <Card 
            title={
              <Space>
                <ThunderboltOutlined className="text-blue-500" />
                <span>视频 API 提供商列表</span>
                <Badge count={videoProviders.filter(p => p.enabled).length} showZero>
                  <Tag color="green">已启用</Tag>
                </Badge>
              </Space>
            }
            extra={
              <Space>
                <Button 
                  type="primary"
                  icon={<PlusOutlined />} 
                  onClick={() => {
                    setCurrentProvider(null);
                    setCurrentApiType('video');
                    providerForm.resetFields();
                    setEditProviderModal(true);
                  }}
                  size="small"
                >
                  添加提供商
                </Button>
                <Button 
                  icon={<ReloadOutlined />} 
                  onClick={loadVideoProviders}
                  size="small"
                >
                  刷新
                </Button>
              </Space>
            }
            styles={{ body: { padding: 0 } }}
            className="mb-4"
          >
            <Table
              columns={videoProviderColumns}
              dataSource={videoProviders}
              rowKey="provider"
              loading={loading}
              pagination={false}
              locale={{ emptyText: '暂无配置的视频 API 提供商' }}
            />
          </Card>

          {/* 使用说明 */}
          <Card title="📖 使用说明">
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="步骤 1">点击提供商的"编辑"按钮，配置 API Key 和相关参数</Descriptions.Item>
              <Descriptions.Item label="步骤 2">点击"测试"按钮验证配置是否正确</Descriptions.Item>
              <Descriptions.Item label="步骤 3">切换"启用"开关，激活该提供商</Descriptions.Item>
              <Descriptions.Item label="步骤 4">调整优先级数字，控制使用顺序（1 = 最高优先级）</Descriptions.Item>
              <Descriptions.Item label="工作原理">
                系统按优先级顺序尝试各提供商，如果高优先级 API 失败，自动切换到下一个可用的提供商
              </Descriptions.Item>
            </Descriptions>
          </Card>
              </>
            )
          },
          {
            key: 'image-api',
            label: (
              <span>
                <PictureOutlined />
                图片 API
                <Badge count={imageProviders.filter((p: any) => p.enabled).length} showZero style={{ marginLeft: 8 }} />
              </span>
            ),
            children: (
              <>
          <Alert
            message="🎨 图片 API 提供商管理"
            description={
              <div>
                <p>• <strong>多提供商支持</strong>：配置火山引擎、DALL-E、Midjourney 等图片生成 API</p>
                <p>• <strong>自动切换</strong>：当主要 API 失败时自动切换到备用 API</p>
                <p>• <strong>优先级控制</strong>：设置不同提供商的使用优先级</p>
              </div>
            }
            type="info"
            showIcon
            className="mb-4"
          />

          {/* 图片提供商列表 */}
          <Card 
            title={
              <Space>
                <ThunderboltOutlined className="text-purple-500" />
                <span>图片 API 提供商列表</span>
                <Badge count={imageProviders.filter((p: any) => p.enabled).length} showZero>
                  <Tag color="green">已启用</Tag>
                </Badge>
              </Space>
            }
            extra={
              <Space>
                <Button 
                  type="primary"
                  icon={<PlusOutlined />} 
                  onClick={() => {
                    setCurrentProvider(null);
                    setCurrentApiType('image');
                    providerForm.resetFields();
                    setEditProviderModal(true);
                  }}
                  size="small"
                >
                  添加提供商
                </Button>
                <Button 
                  icon={<ReloadOutlined />} 
                  onClick={loadImageProviders}
                  size="small"
                >
                  刷新
                </Button>
              </Space>
            }
            styles={{ body: { padding: 0 } }}
            className="mb-4"
          >
            <Table
              dataSource={imageProviders}
              columns={[
                {
                  title: '提供商',
                  dataIndex: 'name',
                  key: 'name',
                  render: (name: string, record: any) => (
                    <Space>
                      <PictureOutlined className="text-purple-500" />
                      <span className="font-semibold">{name || record.provider}</span>
                      <Tag color={record.enabled ? 'green' : 'default'}>
                        {record.enabled ? '已启用' : '未启用'}
                      </Tag>
                    </Space>
                  )
                },
                {
                  title: '优先级',
                  dataIndex: 'priority',
                  key: 'priority',
                  width: 120,
                  render: (priority: number, record: any) => (
                    <InputNumber
                      min={1}
                      max={99}
                      value={priority}
                      onChange={(value) => handleImageProviderPriorityChange(record.provider, value || 1)}
                      disabled={!record.enabled}
                      size="small"
                      style={{ width: '80px' }}
                    />
                  )
                },
                {
                  title: '配置模型',
                  dataIndex: 'models',
                  key: 'models',
                  width: 200,
                  render: (models: string[], record: any) => (
                    <div className="flex flex-wrap gap-1">
                      {models && models.length > 0 ? (
                        models.map((model, index) => (
                          <Tag key={index} color="purple" className="text-xs">
                            {model}
                          </Tag>
                        ))
                      ) : (
                        <span className="text-gray-400 text-xs">暂无配置模型</span>
                      )}
                    </div>
                  )
                },
                {
                  title: '积分消耗',
                  key: 'creditCosts',
                  width: 180,
                  render: (_: any, record: any) => (
                    <Space.Compact style={{ width: '150px' }}>
                      <InputNumber
                        min={1}
                        max={1000}
                        value={record.creditCosts?.textToImage || 10}
                        onChange={(value) => handleImageCreditCostChange(record.provider, 'textToImage', value || 10)}
                        size="small"
                        style={{ width: '100%' }}
                      />
                      <Button disabled size="small" style={{ width: '50px' }}>积分</Button>
                    </Space.Compact>
                  )
                },
                {
                  title: '状态',
                  key: 'status',
                  width: 100,
                  render: (_: any, record: any) => (
                    <Switch
                      checked={record.enabled || false}
                      onChange={(checked) => handleToggleImageProvider(record.provider, checked)}
                      checkedChildren="启用"
                      unCheckedChildren="禁用"
                      size="small"
                    />
                  )
                },
                {
                  title: '操作',
                  key: 'action',
                  width: 200,
                  render: (_: any, record: any) => (
                    <Space size="small">
                      <Button
                        type="primary"
                        size="small"
                        icon={<RocketOutlined />}
                        onClick={() => handleTestImageProvider(record.provider)}
                        disabled={!record.enabled || !record.config?.apiKey}
                        title={!record.enabled ? '请先启用提供商' : !record.config?.apiKey ? '请先配置 API Key' : '测试连接'}
                      >
                        测试
                      </Button>
                      <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEditImageProvider(record)}
                      >
                        配置
                      </Button>
                      <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteImageProvider(record.provider)}
                        disabled={record.enabled}
                        title={record.enabled ? '请先禁用提供商' : '删除提供商'}
                      >
                        删除
                      </Button>
                    </Space>
                  )
                }
              ]}
              rowKey="provider"
              pagination={false}
              locale={{ emptyText: '暂无配置的图片 API 提供商' }}
            />
          </Card>

          {/* 使用说明 */}
          <Card title="📖 配置说明">
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Alert
                message="✅ Grsai Nano Banana 已集成"
                description={
                  <div>
                    <p>• 支持文生图和图生图，速度快，质量高</p>
                    <p>• 支持多种宽高比：auto, 1:1, 16:9, 9:16, 4:3, 3:4, 3:2, 2:3, 5:4, 4:5, 21:9</p>
                    <p>• 两种模型：nano-banana-fast (快速) 和 nano-banana (标准)</p>
                    <p><strong>配置步骤：</strong></p>
                    <ol>
                      <li>点击对应提供商的"配置"按钮</li>
                      <li>填写 API Key 和 Host (国内推荐: https://grsai.dakka.com.cn)</li>
                      <li>选择模型 (推荐: nano-banana-fast)</li>
                      <li>保存后，启用提供商开关即可使用</li>
                    </ol>
                  </div>
                }
                type="success"
                showIcon
              />
              <Alert
                message="其他提供商"
                description="火山引擎、DALL-E 3 等提供商功能正在开发中，敬请期待。"
                type="info"
                showIcon
              />
            </Space>
          </Card>
              </>
            )
          },
          {
            key: 'text-api',
            label: (
              <span>
                <FileTextOutlined />
                文字 API
                <Badge count={textProviders.filter((p: any) => p.enabled).length} showZero style={{ marginLeft: 8 }} />
              </span>
            ),
            children: (
              <>
          <Alert
            message="📝 文字 API 提供商管理"
            description={
              <div>
                <p>• <strong>多模型支持</strong>：配置 DeepSeek、GPT-4、Claude、文心一言等文本生成 API</p>
                <p>• <strong>智能路由</strong>：根据任务类型自动选择最适合的模型</p>
                <p>• <strong>成本优化</strong>：优先使用性价比高的 API</p>
              </div>
            }
            type="info"
            showIcon
            className="mb-4"
          />

          {/* 文字提供商列表 */}
          <Card 
            title={
              <Space>
                <ThunderboltOutlined className="text-green-500" />
                <span>文字 API 提供商列表</span>
                <Badge count={textProviders.filter((p: any) => p.enabled).length} showZero>
                  <Tag color="green">已启用</Tag>
                </Badge>
              </Space>
            }
            extra={
              <Space>
                <Button 
                  type="primary"
                  icon={<PlusOutlined />} 
                  onClick={() => {
                    setCurrentProvider(null);
                    setCurrentApiType('text');
                    providerForm.resetFields();
                    setEditProviderModal(true);
                  }}
                  size="small"
                >
                  添加提供商
                </Button>
                <Button 
                  icon={<ReloadOutlined />} 
                  onClick={loadTextProviders}
                  size="small"
                >
                  刷新
                </Button>
              </Space>
            }
            styles={{ body: { padding: 0 } }}
            className="mb-4"
          >
            <Table
              dataSource={textProviders}
              columns={[
                {
                  title: '提供商',
                  dataIndex: 'name',
                  key: 'name',
                  render: (name: string, record: any) => (
                    <Space>
                      <FileTextOutlined className="text-green-500" />
                      <span className="font-semibold">{name || record.provider}</span>
                      <Tag color={record.enabled ? 'green' : 'default'}>
                        {record.enabled ? '已启用' : '未启用'}
                      </Tag>
                    </Space>
                  )
                },
                {
                  title: '优先级',
                  dataIndex: 'priority',
                  key: 'priority',
                  width: 120,
                  render: (priority: number, record: any) => (
                    <InputNumber
                      min={1}
                      max={99}
                      value={priority}
                      onChange={(value) => handleTextProviderPriorityChange(record.provider, value || 1)}
                      disabled={!record.enabled}
                      size="small"
                      style={{ width: '80px' }}
                    />
                  )
                },
                {
                  title: '配置模型',
                  dataIndex: 'models',
                  key: 'models',
                  width: 200,
                  render: (models: string[], record: any) => (
                    <div className="flex flex-wrap gap-1">
                      {models && models.length > 0 ? (
                        models.map((model, index) => (
                          <Tag key={index} color="green" className="text-xs">
                            {model}
                          </Tag>
                        ))
                      ) : (
                        <span className="text-gray-400 text-xs">暂无配置模型</span>
                      )}
                    </div>
                  )
                },
                {
                  title: '积分消耗',
                  key: 'creditCosts',
                  width: 180,
                  render: (_: any, record: any) => (
                    <Space.Compact style={{ width: '150px' }}>
                      <InputNumber
                        min={1}
                        max={1000}
                        value={record.creditCosts?.textGeneration || 5}
                        onChange={(value) => handleTextCreditCostChange(record.provider, 'textGeneration', value || 5)}
                        size="small"
                        style={{ width: '100%' }}
                      />
                      <Button disabled size="small" style={{ width: '50px' }}>积分</Button>
                    </Space.Compact>
                  )
                },
                {
                  title: '状态',
                  key: 'status',
                  width: 100,
                  render: (_: any, record: any) => (
                    <Switch
                      checked={record.enabled || false}
                      onChange={(checked) => handleToggleTextProvider(record.provider, checked)}
                      checkedChildren="启用"
                      unCheckedChildren="禁用"
                      size="small"
                    />
                  )
                },
                {
                  title: '操作',
                  key: 'action',
                  width: 200,
                  render: (_: any, record: any) => (
                    <Space size="small">
                      <Button
                        type="primary"
                        size="small"
                        icon={<RocketOutlined />}
                        onClick={() => handleTestTextProvider(record.provider)}
                        disabled={!record.enabled || !record.config?.apiKey}
                        title={!record.enabled ? '请先启用提供商' : !record.config?.apiKey ? '请先配置 API Key' : '测试连接'}
                      >
                        测试
                      </Button>
                      <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEditTextProvider(record)}
                      >
                        配置
                      </Button>
                      <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteTextProvider(record.provider)}
                        disabled={record.enabled}
                        title={record.enabled ? '请先禁用提供商' : '删除提供商'}
                      >
                        删除
                      </Button>
                    </Space>
                  )
                }
              ]}
              rowKey="provider"
              pagination={false}
              locale={{ emptyText: '暂无配置的文字 API 提供商' }}
            />
          </Card>

          {/* 使用说明 */}
          <Card title="📖 使用说明">
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="步骤 1">点击"添加提供商"或提供商的"配置"按钮</Descriptions.Item>
              <Descriptions.Item label="步骤 2">填写 API Key 和相关参数</Descriptions.Item>
              <Descriptions.Item label="步骤 3">点击"测试"按钮验证配置是否正确</Descriptions.Item>
              <Descriptions.Item label="步骤 4">切换"启用"开关，激活该提供商</Descriptions.Item>
              <Descriptions.Item label="支持的提供商">
                DeepSeek、OpenAI GPT-4、Claude 3.5、文心一言、通义千问等
              </Descriptions.Item>
            </Descriptions>
          </Card>
              </>
            )
          }
        ]}
      />

      {/* 编辑/添加提供商模态框 */}
      <Modal
        title={
          <span>
            {currentApiType === 'video' && <VideoCameraOutlined className="mr-2" />}
            {currentApiType === 'image' && <PictureOutlined className="mr-2" />}
            {currentApiType === 'text' && <FileTextOutlined className="mr-2" />}
            {currentProvider 
              ? `编辑 ${currentProvider.provider} 配置` 
              : `添加${currentApiType === 'video' ? '视频' : currentApiType === 'image' ? '图片' : '文字'} API 提供商`
            }
          </span>
        }
        open={editProviderModal}
        onOk={() => providerForm.submit()}
        onCancel={() => {
          setEditProviderModal(false);
          providerForm.resetFields();
          setCurrentProvider(null);
        }}
        confirmLoading={loading}
        width={700}
      >
        <Form form={providerForm} layout="vertical" onFinish={handleSaveVideoProvider}>
          {/* 添加新提供商时需要输入 ID */}
          {!currentProvider && (
            <>
              <Alert
                message="添加新提供商"
                description="请选择或输入提供商 ID（如 grsai、minimax、doubao 等），ID 一旦创建不可修改"
                type="info"
                showIcon
                className="mb-4"
              />
              
              <Form.Item
                label="提供商 ID"
                name="provider"
                rules={[
                  { required: true, message: '请输入提供商 ID' },
                  { pattern: /^[a-z0-9_-]+$/, message: '只能包含小写字母、数字、下划线和横线' }
                ]}
              >
                <Select
                  placeholder="选择或输入提供商 ID"
                  allowClear
                  showSearch
                  mode="tags"
                  maxCount={1}
                  options={
                    currentApiType === 'video' ? [
                    { label: 'Grsai Sora2', value: 'grsai' },
                    { label: 'Minimax 海螺视频', value: 'minimax' },
                    { label: '豆包视频', value: 'doubao' },
                    { label: 'Runway Gen-2', value: 'runway' },
                    { label: '自定义', value: 'custom' }
                    ] : currentApiType === 'image' ? [
                      { label: 'Grsai Nano Banana', value: 'grsai' },
                      { label: '火山引擎', value: 'volcengine' },
                      { label: 'DALL-E 3', value: 'dalle3' },
                      { label: 'Midjourney', value: 'midjourney' },
                      { label: '自定义', value: 'custom' }
                    ] : [
                      { label: 'DeepSeek', value: 'deepseek' },
                      { label: 'OpenAI GPT-4', value: 'openai' },
                      { label: 'Claude 3.5', value: 'claude' },
                      { label: '文心一言', value: 'wenxin' },
                      { label: '通义千问', value: 'qwen' },
                      { label: '自定义', value: 'custom' }
                    ]
                  }
                />
              </Form.Item>

              <Form.Item
                label="优先级"
                name="priority"
                initialValue={99}
                tooltip="数字越小优先级越高，建议新提供商先设为 99"
              >
                <InputNumber min={1} max={99} style={{ width: '100%' }} />
              </Form.Item>

              <Divider />
            </>
          )}

          <Form.Item
            label="API 地址"
            name="host"
            rules={[{ required: true, message: '请输入 API 地址' }]}
          >
            <Input placeholder="https://api.example.com" />
          </Form.Item>

          <Form.Item
            label="API Key"
            name="apiKey"
            rules={[{ required: true, message: '请输入 API Key' }]}
          >
            <Input.Password placeholder="请输入 API Key" />
          </Form.Item>

          <Form.Item
            label="默认模型"
            name="model"
            tooltip="留空则使用提供商的默认模型"
          >
            <Input placeholder="例: sora-2" />
          </Form.Item>

          {currentProvider && (
            <Alert
              message="提示"
              description="修改配置后请务必点击【测试】按钮验证配置是否正确。"
              type="info"
              showIcon
            />
          )}

          {!currentProvider && (
            <Alert
              message="添加后的操作"
              description={
                <ol className="list-decimal list-inside space-y-1">
                  <li>保存后提供商会添加到列表</li>
                  <li>点击"测试"按钮验证配置</li>
                  <li>测试成功后切换"启用"开关</li>
                  <li>调整优先级控制使用顺序</li>
                </ol>
              }
              type="success"
              showIcon
            />
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default APIConfigManager;
